import { createContext, type ReactNode, useRef } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { type TraBase } from '@ics-portal/tra';
import { LOCK_TRA, UNLOCK_TRA } from '@api/queries/tra';
import { useToast } from '@contexts/GlobalUiContext';
import { TraStatus, type User } from '@gql/graphql';
import createApolloClient from '@api/createApolloClient';
import useUser from '@hooks/useUser';
import msalClient from '@auth/msal/client';
import useTRA from '../hooks/use-tra';

export interface LockedBy {
    user?: User;
    at?: Date;
    locked: boolean;
}

export interface TRALockContextValues {
    traLocked?: LockedBy;
}

export function isTraLockedForUser(
    lockedById: string | undefined,
    userId: string | undefined,
): boolean {
    return Boolean(lockedById && lockedById !== userId);
}

const TRALockContext = createContext<TRALockContextValues>({});

export function TRALockProvider<T extends TraBase>({
    children,
}: {
    children: ReactNode;
}): ReactNode {
    // Create a new Apollo client as it should not affect rendering but just be a background side effect
    const client = createApolloClient(msalClient, import.meta.env.VITE_API_URI);

    const { tra } = useTRA<T>();
    const user = useUser();
    const { pushErrorToast } = useToast();
    const [lockTra] = useMutation(LOCK_TRA, {
        client,
    });
    const [unlockTra] = useMutation(UNLOCK_TRA, { client });

    const interval = useRef<NodeJS.Timeout | undefined>();

    const [lockedState, setLockedState] = useState<LockedBy>();

    const handleLock = useCallback(() => {
        if (!tra?.id) {
            return;
        }
        console.info('%cAcquire document lock for current user', 'color:red');
        lockTra({ variables: { id: tra.id } })
            .then((res) => {
                setLockedState({
                    user: res.data?.lockTRA.lockedBy as User | undefined,
                    at: res.data?.lockTRA.lockedAt
                        ? new Date(res.data.lockTRA.lockedAt)
                        : undefined,
                    locked: isTraLockedForUser(
                        res.data?.lockTRA.lockedBy?.id,
                        user?.id,
                    ),
                });
            })
            .catch((e) => {
                pushErrorToast({
                    detail: `Unable to acquire lock on TRA document. ${e.message}`,
                });
            });
    }, [lockTra, pushErrorToast, tra?.id, user?.id]);

    const handleUnlock = useCallback(() => {
        if (!tra?.id) {
            return;
        }
        console.info('%cUnlocking document', 'color:green');
        unlockTra({ variables: { id: tra.id } }).catch(() => {
            pushErrorToast({
                detail: 'Unable to release lock on TRA document',
            });
        });
    }, [tra?.id, unlockTra, pushErrorToast]);

    /**
     * Set initial locked state
     */
    useEffect(() => {
        if (tra?.id) {
            setLockedState({
                user: tra.lockedBy ? tra.lockedBy : undefined,
                at: tra.lockedAt ? new Date(tra.lockedAt) : undefined,
                locked: isTraLockedForUser(tra.lockedBy?.id, user?.id),
            });
        }
    }, [tra?.id, tra?.lockedAt, tra?.lockedBy, user?.id]);

    useEffect(() => {
        if (
            !user?.id ||
            Boolean(tra?.lockedBy?.id && tra.lockedBy.id !== user.id) ||
            (tra?.status &&
                ![TraStatus.Inprogress, TraStatus.Declined].includes(
                    tra.status,
                ))
        ) {
            return;
        }
        if (interval.current) {
            clearInterval(interval.current);
        }
        handleLock();
        interval.current = setInterval(handleLock, 1000 * 60);
    }, [handleLock, tra?.id, user?.id, tra?.status, tra?.lockedBy?.id]);

    useEffect(() => {
        return () => {
            if (!interval.current) {
                return;
            }
            handleUnlock();
            clearInterval(interval.current);
        };
    }, [handleUnlock]);

    const value = useMemo<TRALockContextValues>(() => {
        return {
            traLocked: lockedState,
        };
    }, [lockedState, user?.id]);

    return (
        <TRALockContext.Provider value={value}>
            {children}
        </TRALockContext.Provider>
    );
}

export default TRALockContext;
